import React from 'react';
import {
  ChatBubbleLeftRightIcon,
  MegaphoneIcon,
  ShieldCheckIcon,
  CursorArrowRaysIcon,
  DocumentTextIcon,
  RectangleStackIcon,
  ShoppingBagIcon,
  ClipboardDocumentListIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import {
  categoryLabel,
  templateTypeLabel,
  type TemplateCategory,
  type TemplateFormState,
  type TemplateType,
} from './whatsappTemplateForm';

interface WhatsAppTemplateCategoryStepProps {
  form: TemplateFormState;
  setForm: React.Dispatch<React.SetStateAction<TemplateFormState>>;
  onContinue: () => void;
}

const CATEGORIES: { id: TemplateCategory; label: string; icon: React.ElementType; description: string }[] =
  [
    {
      id: 'MARKETING',
      label: 'Marketing',
      icon: MegaphoneIcon,
      description: 'Promotions, offers, and re-engagement messages',
    },
    {
      id: 'UTILITY',
      label: 'Utility',
      icon: ChatBubbleLeftRightIcon,
      description: 'Transactional updates, alerts, and account info',
    },
    {
      id: 'AUTHENTICATION',
      label: 'Authentication',
      icon: ShieldCheckIcon,
      description: 'OTP and login verification codes',
    },
  ];

type TemplateTypeCard = {
  id: TemplateType | 'CAROUSEL' | 'CATALOG' | 'FORM' | 'WHATSAPP_PAY';
  label: string;
  description: string;
  icon: React.ElementType;
  enabled: boolean;
};

const TEMPLATE_TYPES: TemplateTypeCard[] = [
  {
    id: 'WITH_BUTTONS',
    label: 'Template with Buttons',
    description: 'Quick replies, website links, phone, and copy code CTAs',
    icon: CursorArrowRaysIcon,
    enabled: true,
  },
  {
    id: 'SIMPLE',
    label: 'Simple template',
    description: 'Header, body, and footer only — no custom buttons',
    icon: DocumentTextIcon,
    enabled: true,
  },
  {
    id: 'CAROUSEL',
    label: 'Carousel',
    description: 'Multiple cards with images and buttons',
    icon: RectangleStackIcon,
    enabled: false,
  },
  {
    id: 'CATALOG',
    label: 'Catalog',
    description: 'Product catalog messages',
    icon: ShoppingBagIcon,
    enabled: false,
  },
  {
    id: 'FORM',
    label: 'Form',
    description: 'Collect responses inside WhatsApp',
    icon: ClipboardDocumentListIcon,
    enabled: false,
  },
  {
    id: 'WHATSAPP_PAY',
    label: 'WhatsApp Pay',
    description: 'Payment request templates',
    icon: CreditCardIcon,
    enabled: false,
  },
];

export const WhatsAppTemplateCategoryStep: React.FC<WhatsAppTemplateCategoryStepProps> = ({
  form,
  setForm,
  onContinue,
}) => {
  const selectedType = form.templateType;

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">Choose category</h3>
        <p className="mt-1 text-sm text-gray-500">Select the Meta template category for approval.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {CATEGORIES.map(({ id, label, icon: Icon, description }) => {
            const selected = form.category === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, category: id }))}
                className={`flex min-w-[140px] flex-1 flex-col items-start rounded-xl border px-4 py-3 text-left transition ${
                  selected
                    ? 'border-[#25D366] bg-[#25D366]/10 ring-1 ring-[#25D366]/30'
                    : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                }`}
              >
                <Icon className={`mb-2 h-5 w-5 ${selected ? 'text-[#128C7E]' : 'text-gray-400'}`} />
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{label}</span>
                <span className="mt-0.5 text-xs text-gray-500">{description}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">Choose template type</h3>
        <p className="mt-1 text-sm text-gray-500">Pick the message layout you want to build.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {TEMPLATE_TYPES.map(({ id, label, description, icon: Icon, enabled }) => {
            const isSelected = enabled && selectedType === id;
            return (
              <label
                key={id}
                className={`relative flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition ${
                  !enabled
                    ? 'cursor-not-allowed border-gray-100 bg-gray-50 opacity-70 dark:border-gray-800 dark:bg-gray-900/40'
                    : isSelected
                      ? 'border-[#25D366] bg-[#25D366]/5 ring-1 ring-[#25D366]/30'
                      : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                }`}
              >
                <input
                  type="radio"
                  name="templateType"
                  className="mt-1"
                  disabled={!enabled}
                  checked={isSelected}
                  onChange={() => {
                    if (!enabled || id === 'CAROUSEL' || id === 'CATALOG' || id === 'FORM' || id === 'WHATSAPP_PAY')
                      return;
                    setForm((prev) => ({
                      ...prev,
                      templateType: id as TemplateType,
                      buttons: id === 'SIMPLE' ? [] : prev.buttons,
                    }));
                  }}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Icon className="h-5 w-5 shrink-0 text-gray-400" />
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{label}</span>
                    {!enabled && (
                      <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        Coming soon
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">{description}</p>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end border-t border-gray-200 pt-4 dark:border-gray-700">
        <button
          type="button"
          onClick={onContinue}
          className="rounded-lg bg-[#25D366] px-5 py-2 text-sm font-semibold text-white hover:bg-[#1da851]"
        >
          Continue — {categoryLabel(form.category)} · {templateTypeLabel(form.templateType)}
        </button>
      </div>
    </div>
  );
};

export default WhatsAppTemplateCategoryStep;
